import useJDialog from '@/hooks/useJDialog';
import { TopBar } from '@/layouts/TopBar';
import MarkAsReadDialogBody from '@/modules/notifications/components/molucules/MarkAsReadDialogBody';
import NotificationCard from '@/modules/notifications/components/molucules/NotificationCard';
import PreferencesDialogBody from '@/modules/notifications/components/molucules/PreferencesDialogBody';
import NotificationStatus from '@/modules/notifications/models/notification-status.enum';
import {
  useDisableNotification,
  useMakeAllAsRead,
} from '@/modules/notifications/mutations/notificationMutations';
import { useGetNotifications } from '@/modules/notifications/queries/useGetNotifications';
import { useGetUserNotifications } from '@/modules/notifications/queries/useGetUserNotifications';
import { JButton } from '@atoms/JButton';
import useAuth from '@authentication/hooks/useAuth';
import IPaginatedParams from '@models/paginated-params';
import { Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const NotificationsPage = () => {
  const [params] = useState<IPaginatedParams>({
    limit: 20,
    page: 1,
  });

  const { t } = useTranslation();
  const { user } = useAuth();
  const { data } = useGetNotifications(params, user?.id ?? '');
  const { data: userNotificationsPreferences } = useGetUserNotifications(
    user?.id,
  );
  const { showJDialog, closeJDialog } = useJDialog();
  const { mutate: disableNotification, isSuccess } = useDisableNotification();
  const { mutate: markAllAsRead, isSuccess: setAsReadSuccess } =
    useMakeAllAsRead();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isSuccess) return;
    queryClient.invalidateQueries(['userNotifications']);
    closeJDialog();
  }, [isSuccess]);
  useEffect(() => {
    if (!setAsReadSuccess) return;
    queryClient.invalidateQueries(['notifications']);
    closeJDialog();
  }, [setAsReadSuccess]);
  const showPreferencesDialog = () => {
    showJDialog(
      t('MANAGE_MY_NOTIFICATIONS'),
      <PreferencesDialogBody
        title={t('CV_VIDEOS')}
        caption={t('RECEIVE_NOTIFICATIONS_WHEN_CV_VIDEO_REQUIRES_APPROVAL')}
        onClose={closeJDialog}
        userId={user?.id}
        userNotificationsResponse={userNotificationsPreferences?.data}
        disableNotificationMutation={disableNotification}
      />,
      'basic',
    );
  };
  const showMarkAsReadDialog = () => {
    showJDialog(
      t('SET_AS_READ'),
      <MarkAsReadDialogBody
        title={t('ARE_YOU_SURE_YOU_WANT_TO_SET_AS_READ_ALL_YOUR_NOTIFICATIONS')}
        onClose={closeJDialog}
        userId={user?.id}
        markAllAsReadMutation={markAllAsRead}
      />,
      'basic',
    );
  };

  return (
    <div>
      <TopBar
        title={t('NOTIFICATIONS')}
        count={data?.data.meta.unreadNotificationCount}
      >
        <div>
          {data?.data.data.filter(
            (e) =>
              e.status === NotificationStatus.UNREAD ||
              e.status === NotificationStatus.UNVIEWED,
          ).length === 0 ? (
            <></>
          ) : (
            <JButton variant="text" onClick={showMarkAsReadDialog}>
              {t('SET_ALL_AS_READ')}
            </JButton>
          )}

          <JButton variant="contained" onClick={showPreferencesDialog}>
            {t('PREFERENCES')}
          </JButton>
        </div>
      </TopBar>

      <Stack mt={2} mx={2} gap={1}>
        {data?.data.data.length === 0 ? (
          <Typography variant="h4">{t('NO_NEW_NOTIFICATION')}</Typography>
        ) : (
          data?.data.data.map((item) => (
            <NotificationCard key={item.id} notification={item} />
          ))
        )}
      </Stack>
    </div>
  );
};

export default NotificationsPage;
